import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { albumPath, photoPath } from '@photos/shared'
import type { AlbumInfo } from '@photos/shared'
import { fetchAlbum } from '../lib/api'
import { mediaUrl } from '../lib/media'
import { MediaKindBadge } from '../components/MediaKindBadge'

/** One album's photos. */
export function Album(): JSX.Element {
  const { id, slug } = useParams<{ id: string; slug?: string }>()
  const navigate = useNavigate()
  const [album, setAlbum] = useState<AlbumInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setAlbum(null)
    setError(null)

    fetchAlbum(id)
      .then((loaded) => {
        if (cancelled) return
        setAlbum(loaded)
        document.title = loaded.title
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })

    return () => {
      cancelled = true
    }
  }, [id])

  // The id resolves; a stale slug is rewritten so shared links survive a rename.
  useEffect(() => {
    if (album && slug !== album.slug) {
      navigate(albumPath(album.id, album.slug), { replace: true })
    }
  }, [album, slug, navigate])

  if (error) {
    return (
      <main className="page page--centered">
        <h1>Not available</h1>
        <p className="muted">{error}</p>
        <Link className="button" to="/">
          All albums
        </Link>
      </main>
    )
  }

  if (!album) {
    return (
      <main className="page page--centered">
        <span className="spinner" aria-hidden="true" />
      </main>
    )
  }

  return (
    <main className="page">
      <header className="page__header">
        <Link className="breadcrumb" to="/">
          ← All albums
        </Link>
        <h1>{album.title}</h1>
        {album.description ? <p className="muted">{album.description}</p> : null}
        <p className="muted">
          {album.photos.length} photo{album.photos.length === 1 ? '' : 's'}
        </p>
      </header>

      {album.photos.length === 0 ? <p className="muted">No photos in this album yet.</p> : null}

      <div className="grid">
        {album.photos.map((photo) => (
          <Link key={photo.id} className="card" to={photoPath(photo.id, photo.slug)}>
            <div className="card__media">
              <img className="card__thumb" src={mediaUrl(photo.thumbUrl)} alt="" loading="lazy" />
              <MediaKindBadge kind={photo.kind} />
            </div>
            <div className="card__body">
              <span className="card__title">{photo.title}</span>
              <span className="card__meta">
                {photo.width.toLocaleString()} × {photo.height.toLocaleString()}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </main>
  )
}
